/**
 * List data for the External Brand Proposals tab: the MA catalogue, the
 * filter-column prefetch, and the WK brands it is matched against.
 *
 * Everything is fetched once per browser session and held in the session
 * cache; mounting this hook again (the tab is unmounted whenever another tab is
 * selected) reuses what is there. Only {@link refreshMaBrands} re-pulls.
 */
import { useEffect } from 'react';
import { getWatchBrandById, getWatchBrands } from '../../services/firebase';
import {
  MaConfigError,
  fetchMaBrandsList,
  prefetchMaListColumns,
} from '../../services/microbrandAtlas';
import type { MaBrandRow, MaListColumnPrefetch } from '../../services/microbrandAtlas.types';
import {
  clearDetailEntries,
  getSessionCache,
  patchSessionCache,
  upsertWkBrand,
  useSessionCache,
} from './sessionCache';

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** Lay the prefetched filter columns onto the list rows, by slug. */
export function mergePrefetch(rows: MaBrandRow[], prefetch: MaListColumnPrefetch[]): MaBrandRow[] {
  const bySlug = new Map(prefetch.map((p) => [p.slug, p]));
  return rows.map((row) => {
    const extra = bySlug.get(row.slug);
    if (!extra) return row;
    const { slug: _slug, ...columns } = extra;
    return { ...row, ...columns };
  });
}

/**
 * Pull the MA list. All-or-nothing: a failed list leaves no rows behind, so the
 * reviewer never triages against half a catalogue. The prefetch is the one
 * tolerated partial failure — the list still works, only its two filters don't.
 */
async function loadMaList() {
  if (getSessionCache().listStatus === 'loading') return;
  patchSessionCache({ listStatus: 'loading', listError: null, listErrorIsConfig: false });
  try {
    let prefetchError: string | null = null;
    const [rows, prefetch] = await Promise.all([
      fetchMaBrandsList(),
      prefetchMaListColumns().catch((error: unknown) => {
        prefetchError = errorMessage(error);
        return [] as MaListColumnPrefetch[];
      }),
    ]);
    patchSessionCache({
      listStatus: 'ready',
      rows: mergePrefetch(rows, prefetch),
      prefetchError,
      listFetchedAt: Date.now(),
    });
  } catch (error) {
    patchSessionCache({
      listStatus: 'error',
      listError: errorMessage(error),
      listErrorIsConfig: error instanceof MaConfigError,
      rows: [],
      prefetchError: null,
      listFetchedAt: null,
    });
  }
}

/**
 * Every WK brand, straight from Firestore. Not the Algolia listing: matching
 * needs `externalRefs` and the alias arrays on every brand, and the index
 * carries a projection.
 */
async function loadWkBrands() {
  if (getSessionCache().wkStatus === 'loading') return;
  patchSessionCache({ wkStatus: 'loading', wkError: null });
  try {
    patchSessionCache({ wkStatus: 'ready', wkBrands: await getWatchBrands() });
  } catch (error) {
    patchSessionCache({ wkStatus: 'error', wkError: errorMessage(error), wkBrands: [] });
  }
}

/** Re-pull everything, including detail rows opened so far. */
export function refreshMaBrands() {
  clearDetailEntries();
  void loadMaList();
  void loadWkBrands();
}

/** Retry just the half that failed. */
export function retryMaList() {
  void loadMaList();
}
export function retryWkBrands() {
  void loadWkBrands();
}

/**
 * Re-read one WK brand into the session copy, without re-reading the whole
 * collection. Used after a Create/Update lands (so the next match pass sees its
 * new `externalRefs` stamp) and when a detail view opens on a matched brand (so
 * edits made elsewhere are picked up). Never rejects: on failure the cached
 * copy stays as it was.
 */
export async function refreshWkBrand(brandId: string) {
  try {
    const brand = await getWatchBrandById(brandId);
    if (brand) upsertWkBrand(brand);
  } catch (error) {
    console.error('Failed to refresh WK brand:', error);
  }
}

export function useMaBrands() {
  const listStatus = useSessionCache((s) => s.listStatus);
  const listError = useSessionCache((s) => s.listError);
  const listErrorIsConfig = useSessionCache((s) => s.listErrorIsConfig);
  const rows = useSessionCache((s) => s.rows);
  const prefetchError = useSessionCache((s) => s.prefetchError);
  const listFetchedAt = useSessionCache((s) => s.listFetchedAt);
  const wkStatus = useSessionCache((s) => s.wkStatus);
  const wkError = useSessionCache((s) => s.wkError);
  const wkBrands = useSessionCache((s) => s.wkBrands);

  useEffect(() => {
    if (getSessionCache().listStatus === 'idle') void loadMaList();
    if (getSessionCache().wkStatus === 'idle') void loadWkBrands();
  }, []);

  return {
    listStatus,
    listError,
    listErrorIsConfig,
    rows,
    prefetchError,
    listFetchedAt,
    wkStatus,
    wkError,
    wkBrands,
  };
}
